import React from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import SwiftUIPreview from '../Tools/StoryboardConverter/SwiftUIPreview';
import useThemeStore from '../../store/useThemeStore';
import CodeBlocksEditor from './CodeBlocksEditor';

const CodeEditor = ({ code, onChange, fileName, showPreview, previewCode }) => {
  const theme = useThemeStore((state) => state.theme);
  
  // Check if code is structured (object with blocks) or legacy (string)
  const isStructuredCode = code && typeof code === 'object' && code.blocks && Array.isArray(code.blocks);
  
  // If structured code and no preview mode, use CodeBlocksEditor
  if (isStructuredCode && !showPreview) {
    return (
      <CodeBlocksEditor 
        codeBlocks={code.blocks} 
        importStatement={code.importStatement || 'import SwiftUI'}
      />
    );
  }
  
  // Legacy string format or preview mode - use existing CodeEditor behavior
  const displayCode = previewCode || (typeof code === 'string' ? code : JSON.stringify(code, null, 2));
  
  // Use JavaScript highlighting for Swift (closest available) or create a basic Swift-like mode
  // CodeMirror doesn't have official Swift support, but JavaScript syntax is close enough
  const languageExtension = javascript();
  
  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-gray-900 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0 transition-colors duration-200">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-300">
          {fileName ? fileName : 'Generated Swift Code'}
        </h3>
        <div className="flex items-center gap-2">
          {showPreview && (
            <span className="text-xs text-gray-500 dark:text-gray-500">
              Preview Mode
            </span>
          )}
          <button
            onClick={async (e) => {
              try {
                await navigator.clipboard.writeText(displayCode);
                // Visual feedback
                const btn = e.target.closest('button');
                const originalText = btn.textContent;
                btn.textContent = 'Copied!';
                btn.classList.add('bg-green-500', 'text-white');
                setTimeout(() => {
                  btn.textContent = originalText;
                  btn.classList.remove('bg-green-500', 'text-white');
                }, 2000);
              } catch (err) {
                console.error('Failed to copy:', err);
              }
            }}
            className="text-xs px-4 py-1.5 bg-gradient-primary hover:opacity-90 rounded-md text-white font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1.5"
            title="Copy code to clipboard"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {showPreview && previewCode && (
          <>
            {/* Preview Panel */}
            <div className="w-full lg:w-1/2 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 flex-shrink-0">
              <SwiftUIPreview swiftCode={previewCode} />
            </div>
            {/* Code Panel */}
            <div className="flex-1 min-h-0">
              <CodeMirror
                value={displayCode}
                height="100%"
                extensions={[languageExtension]}
                theme={theme === 'dark' ? oneDark : undefined}
                editable={false}
                onChange={onChange}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  dropCursor: false,
                  allowMultipleSelections: false,
                }}
              />
            </div>
          </>
        )}
        {(!showPreview || !previewCode) && (
          <CodeMirror
            value={displayCode}
            height="100%"
            extensions={[languageExtension]}
            theme={theme === 'dark' ? oneDark : undefined}
            editable={false}
            onChange={onChange}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              dropCursor: false,
              allowMultipleSelections: false,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CodeEditor;

